import type { DefaultError, QueryClient, QueryKey } from '@tanstack/query-core'
import {
  DefinedInitialDataInfiniteOptions,
  DefinedUseInfiniteQueryResult,
  InfiniteData,
  UndefinedInitialDataInfiniteOptions,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query'

import { useSession } from './session'

export function useApiInfiniteQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UndefinedInitialDataInfiniteOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
  queryClient?: QueryClient
): UseInfiniteQueryResult<TData, TError>

export function useApiInfiniteQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: DefinedInitialDataInfiniteOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
  queryClient?: QueryClient
): DefinedUseInfiniteQueryResult<TData, TError>

export function useApiInfiniteQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey, TPageParam>,
  queryClient?: QueryClient
): UseInfiniteQueryResult<TData, TError>

export function useApiInfiniteQuery(options: UseInfiniteQueryOptions, queryClient?: QueryClient) {
  const { user, invalidate } = useSession()

  options.enabled = options.enabled === false ? false : !!user

  const qf = options.queryFn
  options.queryFn = async (ctx) => {
    try {
      return await (qf as any)(ctx)
    } catch (error) {
      if (error && (error as any).status === 401) {
        console.log('API ERROR', qf, error)
        invalidate()
      } else {
        console.error('API ERROR', qf, error)
      }
      throw error
    }
  }

  return useInfiniteQuery(options, queryClient)
}
